import { useMemo, useState } from "react";
import * as api from "../lib/api";
import { useStore } from "../lib/store";
import * as sync from "../lib/sync";
import type { Note } from "../lib/types";

/**
 * Quick capture and the approval queue behind it.
 *
 * During listening there are about five seconds of attention available, so the
 * capture box takes a bare word and nothing else. Enrichment fills in the rest
 * later, and nothing enters the review queue until it is approved here.
 */
export default function Inbox() {
  const { lang, notes, reload, syncNow, online } = useStore();
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<Note>>>({});

  const pending = useMemo(
    () =>
      notes
        .filter((n) => n.lang === lang && n.status === "pending")
        .sort((a, b) => b.created_at - a.created_at),
    [notes, lang],
  );

  const needsEnrichment = pending.filter((n) => !n.translation.trim());
  const ready = pending.filter((n) => n.translation.trim());

  function valueOf(note: Note, field: keyof Note): string {
    const edited = edits[note.id]?.[field];
    if (edited !== undefined && edited !== null) return String(edited);
    const original = note[field];
    return original === null || original === undefined ? "" : String(original);
  }

  async function addWord() {
    const text = word.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const { queued } = await sync.captureWord(lang, text);
      setWord("");
      setMessage(queued ? `Saved “${text}” — it will upload when you are back online.` : `Captured “${text}”.`);
      if (!queued) await syncNow();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function fillIn() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.enrich(needsEnrichment.map((n) => n.id));
      await syncNow();
      setMessage(`Filled in ${result.enriched} item${result.enriched === 1 ? "" : "s"}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function approveAll() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const patches = Object.entries(edits)
        .filter(([id]) => ready.some((n) => n.id === id))
        .map(([id, patch]) => ({ id, ...patch }));
      if (patches.length > 0) await api.updateNotes(patches);
      const result = await api.activateNotes(ready.map((n) => n.id));
      setEdits({});
      await syncNow();
      await reload();
      setMessage(`${result.activated} card${result.activated === 1 ? "" : "s"} added to the queue.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    setBusy(true);
    try {
      await api.deleteNotes([id]);
      await syncNow();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Capture</h1>
      <p className="lede">
        Heard a word? Put it here and keep listening. Translations get filled in later.
      </p>

      <div className="row">
        <input
          type="text"
          placeholder={lang === "fr" ? "un mot…" : "una palabra…"}
          value={word}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void addWord();
          }}
        />
        <button className="btn primary" disabled={busy || !word.trim()} onClick={() => void addWord()}>
          Add
        </button>
      </div>

      {message && <div className="notice ok" style={{ marginTop: 10 }}>{message}</div>}
      {error && <div className="notice error" style={{ marginTop: 10 }}>{error}</div>}

      {needsEnrichment.length > 0 && (
        <>
          <h2>Needs filling in ({needsEnrichment.length})</h2>
          <div className="stack">
            {needsEnrichment.map((note) => (
              <div key={note.id} className="noterow">
                <div className="body">
                  <div className="term">{note.term}</div>
                  <div className="translation">—</div>
                </div>
                <button className="btn small ghost danger" onClick={() => void discard(note.id)}>✕</button>
              </div>
            ))}
          </div>
          <button
            className="btn primary full"
            style={{ marginTop: 10 }}
            disabled={busy || !online}
            onClick={() => void fillIn()}
          >
            {busy ? <><span className="spinner" /> Working…</> : `Fill in ${needsEnrichment.length} item${needsEnrichment.length === 1 ? "" : "s"}`}
          </button>
        </>
      )}

      {ready.length > 0 && (
        <>
          <h2>Ready to approve ({ready.length})</h2>
          <p className="lede" style={{ marginTop: -4 }}>Edit anything that came back wrong, then approve.</p>
          <div className="stack">
            {ready.map((note) => (
              <div key={note.id} className="candidate">
                <div className="body stack">
                  <input
                    type="text"
                    value={valueOf(note, "term")}
                    aria-label="Term"
                    onChange={(e) =>
                      setEdits((c) => ({ ...c, [note.id]: { ...c[note.id], term: e.target.value } }))
                    }
                  />
                  <input
                    type="text"
                    value={valueOf(note, "translation")}
                    aria-label="Translation"
                    onChange={(e) =>
                      setEdits((c) => ({ ...c, [note.id]: { ...c[note.id], translation: e.target.value } }))
                    }
                  />
                  {note.example && <div className="example">{note.example}</div>}
                  {note.note && <div className="usage">{note.note}</div>}
                  <div className="row">
                    <button className="btn small ghost danger" onClick={() => void discard(note.id)}>Discard</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="stickybar">
            <button className="btn primary full" disabled={busy || !online} onClick={() => void approveAll()}>
              Approve {ready.length} item{ready.length === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}

      {pending.length === 0 && (
        <div className="empty" style={{ paddingTop: 30 }}>
          <p>Nothing waiting.</p>
        </div>
      )}
    </>
  );
}
